import { Injectable, signal } from '@angular/core';

export type TipoDeAviso = 'exito' | 'error' | 'info';

export interface Aviso {
  id: number;
  tipo: TipoDeAviso;
  texto: string;
  /** Un renglón chico debajo, para el detalle que no entra en el título */
  detalle?: string;
  /** Cuánto dura, en milisegundos. La barra de abajo cuenta esto. */
  duracion: number;
}

/**
 * Los avisos que aparecen arriba a la derecha.
 *
 * Hasta ahora la app **no confirmaba nada**: se guardaba y no pasaba nada en
 * pantalla, y los errores solo se veían si uno estaba mirando la pestaña donde
 * salía el banner. Con el guardado diferido eso se volvió peor: se aprieta
 * Guardar, la barra desaparece y no queda ninguna señal de que salió bien.
 *
 * Reglas:
 *
 *   - Los de **éxito e info se van solos**. El de **error no**: hay algo que
 *     leer y casi siempre algo que hacer, y que se borre mientras uno lee es
 *     la peor forma de enterarse.
 *   - Se apilan, el último arriba, con un tope: veinte avisos encimados tapan
 *     la pantalla y ninguno se lee.
 */
@Injectable({ providedIn: 'root' })
export class AvisosService {
  /** Más de esto y dejan de leerse: se descarta el más viejo */
  private static readonly MAXIMO = 4;

  private static readonly DURACION: Record<TipoDeAviso, number> = {
    exito: 4000,
    info: 5000,
    /* Tres veces más: un error trae algo que leer y algo que hacer. Antes era
       cero —no se iba nunca— y había que cerrarlo a mano. */
    error: 12000
  };

  private secuencia = 0;

  readonly avisos = signal<Aviso[]>([]);

  exito(texto: string, detalle?: string): void {
    this.mostrar('exito', texto, detalle);
  }

  error(texto: string, detalle?: string): void {
    this.mostrar('error', texto, detalle);
  }

  info(texto: string, detalle?: string): void {
    this.mostrar('info', texto, detalle);
  }

  cerrar(id: number): void {
    this.frenar(id);
    this.avisos.update((as) => as.filter((a) => a.id !== id));
  }

  /** Los relojes en curso, para poder frenarlos al pasar el mouse */
  private relojes = new Map<number, ReturnType<typeof setTimeout>>();

  private mostrar(tipo: TipoDeAviso, texto: string, detalle?: string): void {
    const id = ++this.secuencia;
    const duracion = AvisosService.DURACION[tipo];

    this.avisos.update(
      (as) => [{ id, tipo, texto, detalle, duracion }, ...as].slice(0, AvisosService.MAXIMO)
    );

    this.arrancarReloj(id, duracion);
  }

  private arrancarReloj(id: number, duracion: number): void {
    if (duracion <= 0) return;

    this.relojes.set(id, setTimeout(() => this.cerrar(id), duracion));
  }

  /**
   * Frena la cuenta mientras el mouse está encima.
   *
   * Que un aviso se borre justo cuando uno lo está leyendo es la peor forma de
   * enterarse de algo. Al salir vuelve a empezar entera: es más generoso que
   * seguir donde estaba, y no necesita llevar la cuenta de lo que faltaba.
   */
  frenar(id: number): void {
    const reloj = this.relojes.get(id);

    if (reloj) {
      clearTimeout(reloj);
      this.relojes.delete(id);
    }
  }

  reanudar(id: number): void {
    const aviso = this.avisos().find((a) => a.id === id);

    if (aviso && !this.relojes.has(id)) {
      this.arrancarReloj(id, aviso.duracion);
    }
  }
}
